import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type ColumnDef, type ColumnFiltersState } from "@tanstack/react-table";
import { PageLayout } from "@/ui/layouts";
import { Button, Card, DialogFooter, Modal } from "@/ui/atoms";
import {
    DataTable,
    EmptyState,
    MasterCodeUsageNotice,
    type TMasterCodeUsage,
} from "@/ui/molecules";
import { useMasterCodeUsage } from "@/useCases/useMasterCodeUsage";
import { useServerTable, type TServerTableQueryInput } from "@/useCases/useServerTable";
import { buildRowCount } from "@/useCases/rowCount";
import type { TApiCoAMasterUploadResponse } from "@/apis/proda_finance/types";
import { BookOpen, Pencil, Plus, RotateCcw, Trash2, Upload } from "lucide-react";
import { ChartOfAccountsFormModal, type TAccountDraft } from "./ChartOfAccountsFormModal";

export type TChartOfAccountsRow = {
    id: string;
    number: string;
    shortName: string;
    category: string | null;
    isProhibited: boolean;
    isActive: boolean;
};

export type TChartOfAccountsQuery = {
    page: number;
    page_size: number;
    name?: string;
    number?: string;
    category?: string;
    is_prohibited?: boolean;
    is_active?: boolean;
    sort_by?: string;
    sort_dir?: "asc" | "desc";
};

const PAGE_SIZE = 50;

const firstString = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;
const asBool = (v: unknown): boolean | undefined => (v === true || v === false ? v : undefined);

const toQuery = (input: TServerTableQueryInput): TChartOfAccountsQuery => {
    const get = (id: string) => input.columnFilters.find((f) => f.id === id)?.value;
    const sort = input.sorting[0];
    return {
        page: input.page,
        page_size: input.pageSize,
        name: firstString(get("name")),
        number: firstString(get("number")),
        category: firstString(get("category")),
        is_prohibited: asBool(get("isProhibited")),
        is_active: asBool(get("isActive")),
        sort_by: sort?.id,
        sort_dir: sort ? (sort.desc ? "desc" : "asc") : undefined,
    };
};

export type TViewChartOfAccountsParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        onQueryChange: (query: TChartOfAccountsQuery, signal?: AbortSignal) => void;
        useChartOfAccountsPage: () => {
            items: TChartOfAccountsRow[];
            total: number;
            totalUnfiltered: number;
            isLoading: boolean;
            hasError: boolean;
        };
        onCreateAccount: (data: TAccountDraft) => Promise<void>;
        onUpdateAccount: (id: string, data: TAccountDraft) => Promise<void>;
        onDeleteAccount: (id: string) => Promise<void>;
        onReactivateAccount: (id: string) => Promise<void>;
        onLoadAccountUsage: (id: string) => Promise<TMasterCodeUsage>;
        onUploadFile: (file: File) => Promise<TApiCoAMasterUploadResponse>;
        permissions: () => string[];
    };
};

export type TViewChartOfAccountsProps = Record<string, never>;

const CATEGORY_OPTIONS = [
    { value: "PL", label: "PL" },
    { value: "BS", label: "BS" },
];

export const createViewChartOfAccounts = ({ useCases }: TViewChartOfAccountsParams) => {
    const ViewChartOfAccounts: React.FC<TViewChartOfAccountsProps> = () => {
        const {
            onMount,
            onUnmount,
            onQueryChange,
            useChartOfAccountsPage,
            onCreateAccount,
            onUpdateAccount,
            onDeleteAccount,
            onReactivateAccount,
            onLoadAccountUsage,
            onUploadFile,
            permissions,
        } = useCases();

        const perms = permissions();
        const canUpload = perms.includes("master_coa_upload");
        const canCreate = perms.includes("settings_coa_line_create");
        const canEdit = perms.includes("settings_coa_line_edit");
        const canDelete = perms.includes("settings_coa_line_delete");

        const page = useChartOfAccountsPage();
        const [isUploading, setIsUploading] = useState(false);
        const [uploadResult, setUploadResult] = useState<TApiCoAMasterUploadResponse | null>(null);
        const fileInputRef = useRef<HTMLInputElement>(null);

        // Modal-driven create/edit/delete (DataTable rows aren't inline-editable).
        const [createOpen, setCreateOpen] = useState(false);
        const [editRow, setEditRow] = useState<TChartOfAccountsRow | null>(null);
        const [deleteRow, setDeleteRow] = useState<TChartOfAccountsRow | null>(null);
        const [isSaving, setIsSaving] = useState(false);
        const [formError, setFormError] = useState<string | null>(null);
        const [reactivatingId, setReactivatingId] = useState<string | null>(null);
        const [reactivateError, setReactivateError] = useState<string | null>(null);
        const deleteUsage = useMasterCodeUsage(deleteRow?.id ?? null, onLoadAccountUsage);

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        const {
            onColumnFiltersChange,
            onSortingChange,
            sorting,
            setPage,
            page: currentPage,
            refetch,
            columnFilters,
        } = useServerTable<TChartOfAccountsQuery>({
            pageSize: PAGE_SIZE,
            toQuery,
            fetchData: onQueryChange,
        });

        // Edit/delete: re-run the current query in place (the affected row is on
        // the page being viewed).
        const refresh = (): void => refetch();
        // Create/upload: a new row (or a replaced dataset) may not belong on the
        // current page, so reset to page 1 and force the fetch.
        const refreshToStart = (): void => {
            setPage(1);
            refetch();
        };

        const submitCreate = async (draft: TAccountDraft): Promise<void> => {
            setFormError(null);
            setIsSaving(true);
            try {
                await onCreateAccount(draft);
                setCreateOpen(false);
                refreshToStart();
            } catch (err) {
                setFormError(err instanceof Error ? err.message : "Failed to create account");
            } finally {
                setIsSaving(false);
            }
        };

        const submitEdit = async (draft: TAccountDraft): Promise<void> => {
            if (!editRow) return;
            setFormError(null);
            setIsSaving(true);
            try {
                await onUpdateAccount(editRow.id, draft);
                setEditRow(null);
                refresh();
            } catch (err) {
                setFormError(err instanceof Error ? err.message : "Failed to update account");
            } finally {
                setIsSaving(false);
            }
        };

        const submitDelete = async (): Promise<void> => {
            if (!deleteRow) return;
            setIsSaving(true);
            setFormError(null);
            try {
                await onDeleteAccount(deleteRow.id);
                setDeleteRow(null);
                refresh();
            } catch (err) {
                setFormError(err instanceof Error ? err.message : "Failed to delete account");
            } finally {
                setIsSaving(false);
            }
        };

        // A deleted account is only soft-deleted, so it can be brought back as-is,
        // with the mappings that still point at it.
        const reactivate = useCallback(
            async (row: TChartOfAccountsRow): Promise<void> => {
                setReactivateError(null);
                setReactivatingId(row.id);
                try {
                    await onReactivateAccount(row.id);
                    refetch();
                } catch (err) {
                    setReactivateError(
                        err instanceof Error ? err.message : `Failed to reactivate ${row.number}`,
                    );
                } finally {
                    setReactivatingId(null);
                }
            },
            [onReactivateAccount, refetch],
        );

        const columns: ColumnDef<TChartOfAccountsRow>[] = useMemo(
            () => [
                {
                    accessorKey: "name",
                    id: "name",
                    header: "Name",
                    meta: { filterType: "text", filterPlaceholder: "Search name…" },
                    cell: ({ row }) => (
                        <span className="font-medium">{row.original.shortName}</span>
                    ),
                },
                {
                    accessorKey: "number",
                    id: "number",
                    header: "Number",
                    meta: { filterType: "text", filterPlaceholder: "Search code…" },
                    cell: ({ row }) => <span className="font-mono">{row.original.number}</span>,
                },
                {
                    accessorKey: "category",
                    id: "category",
                    header: "Category",
                    meta: { filterType: "select", filterOptions: CATEGORY_OPTIONS },
                    cell: ({ row }) =>
                        row.original.category ? (
                            <span className="text-xs font-medium">{row.original.category}</span>
                        ) : (
                            <span className="text-muted-foreground">-</span>
                        ),
                },
                {
                    accessorKey: "isProhibited",
                    id: "isProhibited",
                    header: "Prohibited",
                    enableSorting: false,
                    meta: { filterType: "boolean" },
                    cell: ({ row }) =>
                        row.original.isProhibited ? (
                            <span className="text-red-600">Yes</span>
                        ) : (
                            <span className="text-emerald-600">No</span>
                        ),
                },
                {
                    accessorKey: "isActive",
                    id: "isActive",
                    header: "Active",
                    meta: { filterType: "boolean" },
                    cell: ({ row }) =>
                        row.original.isActive ? (
                            <span className="text-emerald-700">Active</span>
                        ) : (
                            <span className="text-slate-500">Inactive</span>
                        ),
                },
                {
                    id: "actions",
                    header: "Actions",
                    enableSorting: false,
                    meta: { hidePerColumnTextSearch: true },
                    cell: ({ row }) => (
                        <div className="inline-flex justify-end gap-2 w-full">
                            {canEdit && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        setFormError(null);
                                        setEditRow(row.original);
                                    }}
                                >
                                    <Pencil className="h-3 w-3 mr-1" />
                                    Edit
                                </Button>
                            )}
                            {canEdit && !row.original.isActive && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={reactivatingId === row.original.id}
                                    onClick={() => void reactivate(row.original)}
                                >
                                    <RotateCcw className="h-3 w-3 mr-1" />
                                    {reactivatingId === row.original.id
                                        ? "Reactivating..."
                                        : "Reactivate"}
                                </Button>
                            )}
                            {canDelete && row.original.isActive && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        setFormError(null);
                                        setDeleteRow(row.original);
                                    }}
                                >
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    Delete
                                </Button>
                            )}
                        </div>
                    ),
                },
            ],
            [canEdit, canDelete, reactivatingId, reactivate],
        );

        const handleFileSelected = async (
            event: React.ChangeEvent<HTMLInputElement>,
        ): Promise<void> => {
            const file = event.target.files?.[0];
            if (!file) return;
            setIsUploading(true);
            setUploadResult(null);
            try {
                setUploadResult(await onUploadFile(file));
                refreshToStart();
            } catch {
                setUploadResult({
                    success: false,
                    imported_count: 0,
                    new_count: 0,
                    updated_count: 0,
                    deactivated_count: 0,
                    skipped_deactivation: [],
                    skipped_count: 0,
                    errors: ["Upload failed. Please try again."],
                    warnings: [],
                    version_number: null,
                });
            } finally {
                setIsUploading(false);
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
        };

        if (page.hasError) {
            return (
                <PageLayout
                    title="Chart of Accounts"
                    description="Manage account codes and categories"
                    icon={<BookOpen className="h-6 w-6" />}
                >
                    <EmptyState
                        variant="error"
                        title="Failed to load chart of accounts"
                        description="An error occurred while loading account data"
                        action={{ label: "Try Again", onClick: () => refetch() }}
                    />
                </PageLayout>
            );
        }

        return (
            <PageLayout
                title="Chart of Accounts"
                description="Manage account codes and categories"
                icon={<BookOpen className="h-6 w-6" />}
                actions={
                    <div className="flex gap-2">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx"
                            className="hidden"
                            onChange={(event) => void handleFileSelected(event)}
                        />
                        {canUpload && (
                            <Button
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isUploading}
                            >
                                <Upload className="mr-2 h-4 w-4" />
                                {isUploading ? "Uploading..." : "Upload Excel"}
                            </Button>
                        )}
                        {canCreate && (
                            <Button
                                onClick={() => {
                                    setFormError(null);
                                    setCreateOpen(true);
                                }}
                            >
                                <Plus className="mr-2 h-4 w-4" />
                                Add New
                            </Button>
                        )}
                    </div>
                }
            >
                {uploadResult && (
                    <div
                        className={`mb-4 rounded-lg border p-4 ${
                            uploadResult.success
                                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                : "border-red-200 bg-red-50 text-red-800"
                        }`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                {uploadResult.success ? (
                                    <>
                                        <p className="font-medium">
                                            Upload successful (v{uploadResult.version_number})
                                        </p>
                                        <p className="mt-1 text-sm">
                                            New: {uploadResult.new_count} · Updated:{" "}
                                            {uploadResult.updated_count} · Deactivated:{" "}
                                            {uploadResult.deactivated_count}
                                        </p>
                                        {uploadResult.skipped_deactivation.length > 0 && (
                                            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-800">
                                                <p className="text-sm font-medium">
                                                    The following codes could not be deactivated —
                                                    they still have active mappings:
                                                </p>
                                                <ul className="mt-1 list-disc pl-5 text-sm">
                                                    {uploadResult.skipped_deactivation.map(
                                                        (code) => (
                                                            <li key={code}>{code}</li>
                                                        ),
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                        {uploadResult.warnings.length > 0 && (
                                            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-800">
                                                <p className="text-sm font-medium">
                                                    Imported with fallback values — check these rows
                                                    in the source file:
                                                </p>
                                                <ul className="mt-1 list-disc pl-5 text-sm">
                                                    {uploadResult.warnings.map((warning, i) => (
                                                        <li key={i}>{warning}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    uploadResult.errors.map((err, i) => (
                                        <p key={i} className="text-sm">
                                            {err}
                                        </p>
                                    ))
                                )}
                            </div>
                            <button
                                onClick={() => setUploadResult(null)}
                                className="text-current opacity-50 hover:opacity-100 ml-4"
                                aria-label="Dismiss"
                            >
                                &times;
                            </button>
                        </div>
                    </div>
                )}

                {reactivateError && (
                    <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                        {reactivateError}
                    </div>
                )}

                <Card className="overflow-hidden">
                    <DataTable
                        columns={columns}
                        data={page.items}
                        loading={page.isLoading}
                        manualFiltering
                        manualSorting
                        sortingState={sorting}
                        onSortingChange={onSortingChange}
                        onColumnFiltersChange={
                            onColumnFiltersChange as (f: ColumnFiltersState) => void
                        }
                        rowNoun={{ one: "account", many: "accounts" }}
                        rowCount={buildRowCount({
                            total: page.total,
                            totalUnfiltered: page.totalUnfiltered,
                            hasActiveFilters: columnFilters.length > 0,
                        })}
                        pagination={{
                            page: currentPage,
                            pageSize: PAGE_SIZE,
                            total: page.total,
                            onPageChange: setPage,
                        }}
                        storageKey="chart-of-accounts"
                        emptyMessage="No chart of accounts match the current filters."
                    />
                </Card>

                <ChartOfAccountsFormModal
                    open={createOpen}
                    mode="create"
                    isSaving={isSaving}
                    error={formError}
                    onSubmit={(draft) => void submitCreate(draft)}
                    onClose={() => setCreateOpen(false)}
                />

                <ChartOfAccountsFormModal
                    open={editRow !== null}
                    mode="edit"
                    initial={
                        editRow
                            ? {
                                  account_code: editRow.number,
                                  account_name: editRow.shortName,
                                  is_prohibited: editRow.isProhibited,
                              }
                            : undefined
                    }
                    isSaving={isSaving}
                    error={formError}
                    onSubmit={(draft) => void submitEdit(draft)}
                    onClose={() => setEditRow(null)}
                />

                <Modal
                    open={deleteRow !== null}
                    onOpenChange={(next) => {
                        if (!next) {
                            setDeleteRow(null);
                            setFormError(null);
                        }
                    }}
                    title="Delete Account"
                    description={
                        deleteRow
                            ? `Delete ${deleteRow.shortName} (${deleteRow.number})? You can reactivate it later from the inactive accounts.`
                            : undefined
                    }
                >
                    <MasterCodeUsageNotice
                        {...deleteUsage}
                        ownerNoun={{ one: "business partner", many: "business partners" }}
                        consequence="Deleting the account does not remove these mappings: files from these business partners keep mapping to it until the mappings are changed."
                    />
                    {formError && (
                        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                            {formError}
                        </div>
                    )}
                    <DialogFooter className="gap-2 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setDeleteRow(null);
                                setFormError(null);
                            }}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={() => void submitDelete()}
                            disabled={isSaving}
                        >
                            {isSaving ? "Deleting..." : "Delete"}
                        </Button>
                    </DialogFooter>
                </Modal>
            </PageLayout>
        );
    };

    return ViewChartOfAccounts;
};
